// ─────────────────────────────────────────────────────────────────────────────
// AU LOCALISATION — single source of truth for both template and AI paths
//
// This module deliberately handles ONLY market === "au". For any other market
// (including "uk"), every function in here is an early-return passthrough so
// the UK code path is provably untouched.
//
// Two responsibilities:
//   1. Terminology translation  (UK → AU strings in line item names/descriptions)
//   2. Rate sanity caps         (per-category ceilings on unitCost in AUD,
//                                 to catch wild outputs from either the template
//                                 multiplier or the AI cost engine)
// ─────────────────────────────────────────────────────────────────────────────

type AuRule = [RegExp, string] | [RegExp, (substring: string, ...args: any[]) => string];
export const AU_TERMINOLOGY: AuRule[] = [
  // Standards & approvals
  [/Building Regulations? Part L( 2021)?/g, "NCC Section J"],
  [/Building Regulations?/g, "National Construction Code (NCC)"],
  [/Building Control/g, "Principal Certifier (PCA)"],
  [/Part L( 2021)?/g, "NCC Section J"],
  [/Part P/g, "AS/NZS 3000 (electrical)"],
  [/Approved Document B/g, "NCC Volume Two — Fire Safety"],
  [/SE calculations/g, "Structural engineer drawings (AS 4100/AS 1170)"],
  [/Electrical Installation Certificate/g, "Compliance Certificate of Electrical Work (CCEW)"],
  [/NICEIC/g, "licensed electrician certification"],

  // Materials — sheeting / insulation / membranes
  [/uPVC guttering/g, "Colorbond guttering"],
  [/uPVC/g, "aluminium"],
  [/concrete tiles to match existing/g, "Colorbond steel sheeting or terracotta tiles to match existing"],
  [/concrete tiles/g, "terracotta or concrete tiles"],
  [/GRP or EPDM/g, "TPO membrane or Colorbond"],
  [/EPDM warm flat roof/g, "TPO warm flat roof"],
  [/PIR insulation/g, "polyiso (PIR) insulation"],
  [/100mm PIR/g, "100mm polyiso (PIR)"],
  [/90mm PIR/g, "90mm polyiso (PIR)"],
  [/120mm PIR/g, "120mm polyiso (PIR)"],
  [/50mm PIR/g, "50mm polyiso (PIR)"],

  // Materials — linings / boards
  [/Plasterboard To Studs/g, "Gyprock To Framing"],
  [/Plasterboard To Ceilings?/g, "Gyprock To Ceilings"],
  [/Plasterboard/g, "Gyprock"],
  [/plasterboard/g, "gyprock"],
  [/Plaster Skim Coat/g, "Set & Sand"],
  [/skim coat/g, "set and sand"],
  [/Two-coat skim/g, "Two-coat set and sand"],
  [/Dot & Dab Plasterboard/g, "Adhesive-Fixed Gyprock"],
  [/dot & dab|dot and dab/g, "adhesive fix"],
  [/T&G Chipboard Flooring/g, "T&G Particleboard Flooring"],
  [/T&G Chipboard/g, "T&G Particleboard"],
  [/Chipboard/g, "Particleboard"],
  [/chipboard/g, "particleboard"],

  // Joinery / trim
  [/MDF or softwood skirting boards/g, "MDF or pine skirting"],
  [/MDF window board/g, "MDF window reveal"],
  [/Window Boards?/g, "Window Reveals"],
  [/window boards?/g, "window reveals"],
  [/window cill/g, "window sill"],
  [/ironmongery/g, "door hardware"],
  [/Ironmongery/g, "Door hardware"],

  // Services
  [/Boiler Relocation/g, "Hot Water Unit Relocation"],
  [/Boiler/g, "Hot water unit"],
  [/boiler/g, "hot water unit"],
  [/Air Source Heat Pump/g, "Reverse-Cycle Heat Pump (Ducted)"],
  [/air source heat pump|ASHP/g, "reverse-cycle heat pump"],
  [/heat pump with buffer cylinder/g, "ducted reverse-cycle system with zoning"],
  [/consumer unit/g, "switchboard"],
  [/Consumer Unit/g, "Switchboard"],
  [/first[- ]fix/g, "rough-in"],
  [/First[- ]fix/g, "Rough-in"],
  [/second[- ]fix/g, "fit-off"],
  [/Second[- ]fix/g, "Fit-off"],

  // Fittings
  [/Velux Rooflights?/g, "Skylights"],
  [/Velux/g, "skylight"],
  [/rooflights?|roof lights?/g, "skylights"],
  [/Cloakroom/g, "Powder Room"],
  [/cloakroom/g, "powder room"],
  [/WC \/ Cloakroom Pack/g, "Powder Room Pack"],

  // Site & prelims — skip bins (with metric sizing)
  [/Skip Hire \(12 Yard Maxi\)/g, "Skip Bin Hire (9m³ Maxi)"],
  [/Skip Hire \(8 ?Yard\)/g, "Skip Bin Hire (6m³)"],
  [/Skip Hire \(6 ?Yard\)/g, "Skip Bin Hire (4.5m³)"],
  [/Skip Hire/g, "Skip Bin Hire"],
  [/(\d+)\s*[- ]?yard\s+(maxi\s+)?skip/gi, (_m, n, maxi) => {
    const yards = parseInt(n, 10);
    const m3 = Math.round((yards * 0.7646) * 10) / 10;
    return `${m3}m³ ${maxi ? "maxi " : ""}skip bin`;
  }],
  [/(\d+)\s*Yard Maxi/g, (_m, n) => {
    const yards = parseInt(n, 10);
    const m3 = Math.round((yards * 0.7646) * 10) / 10;
    return `${m3}m³ Maxi`;
  }],
  [/Skips? for/g, "Skip bins for"],
  [/multiple skips/gi, "multiple skip bins"],
  [/skip hire/g, "skip bin hire"],

  // Approvals / drawings
  [/Architectural Drawings/g, "Architectural / Drafting Drawings (DA package)"],
  [/planning permission/gi, "Development Approval (DA)"],
  [/permitted development/gi, "Complying Development (CDC)"],

  // Roofing & drainage
  [/Roofline/g, "Fascia & Gutter Line"],
  [/fascias, soffits and guttering in uPVC/g, "Colorbond fascia, eaves lining and guttering"],
  [/Rainwater Goods/g, "Stormwater Goods"],
  [/rainwater goods/g, "stormwater goods"],

  // Project type wording — last so it doesn't break other rules
  [/Loft Conversion/g, "Attic Conversion"],
  [/loft conversion/g, "attic conversion"],
];

// ─────────────────────────────────────────────────────────────────────────────
// Per-category AU rate sanity caps (AUD, supply + fix, ex-GST)
//
// These are CEILINGS. If the generated unitCost for a known category exceeds
// the ceiling, we clamp it. This catches both the template's blanket 2.0×
// multiplier overshooting on labour-light items AND the AI generating wild
// numbers for categories with reasonably tight market rates.
//
// Calibrate against real won AU quotes. Keep slightly above realistic top-end
// so we only catch outliers, not legitimate premium rates.
// ─────────────────────────────────────────────────────────────────────────────
export interface RateCap {
  units: string[];           // unit names this cap applies to
  maxUnitCost: number;       // AUD per unit
}

export const AU_RATE_CAPS: Record<string, RateCap> = {
  plastering: { units: ["sqm", "m²", "m2"], maxUnitCost: 75 },         // gyprock supply+fix typically $35–55/m²
  decorating: { units: ["sqm", "m²", "m2"], maxUnitCost: 45 },         // 2-coat paint $25–35/m²
  insulation: { units: ["sqm", "m²", "m2"], maxUnitCost: 55 },         // R3.5 batts + install $25–40/m²
  flooring: { units: ["sqm", "m²", "m2"], maxUnitCost: 160 },          // laminate to engineered timber
  wall_tiling: { units: ["sqm", "m²", "m2"], maxUnitCost: 120 },       // wall ceramic/porcelain supply+fix
  floor_tiling: { units: ["sqm", "m²", "m2"], maxUnitCost: 150 },      // floor ceramic/porcelain supply+fix
  tiling: { units: ["sqm", "m²", "m2"], maxUnitCost: 150 },            // generic tiling fallback
  waterproofing: { units: ["sqm", "m²", "m2"], maxUnitCost: 110 },     // tanking / membrane to wet area
  carpentry_trim: { units: ["lm", "m", "linear_m", "linear m", "lin m", "lin_m", "lin. m", "l/m", "linear metre", "linear meter", "linear metres", "linear meters"], maxUnitCost: 80 }, // skirting/architrave per lm
  brickwork: { units: ["sqm", "m²", "m2"], maxUnitCost: 280 },         // brick veneer supply+lay
  sanitaryware_install: { units: ["item", "each", "no", "no.", "nr"], maxUnitCost: 2400 },   // labour-only full suite
  bath_accessories_install: { units: ["item", "each", "no", "no.", "nr"], maxUnitCost: 1200 }, // suite of 4-5 accessories
  shower_screen_install: { units: ["item", "each", "no", "no.", "nr"], maxUnitCost: 900 },   // labour-only
  towel_rail_install: { units: ["item", "each", "no", "no.", "nr"], maxUnitCost: 900 },      // labour-only
  skip_bin: { units: ["item", "each", "no", "no.", "nr"], maxUnitCost: 1000 },               // single skip on small refurb
};

// Map common category strings to a RateCap key
function resolveCapKey(category?: string, name?: string): keyof typeof AU_RATE_CAPS | null {
  const c = (category || "").toLowerCase();
  const n = (name || "").toLowerCase();

  if (c.includes("plaster") || c.includes("gyprock") || n.includes("plasterboard") || n.includes("gyprock") || n.includes("plastering")) return "plastering";
  if (c.includes("decorat") || c.includes("paint") || n.includes("decorating") || n.includes("painting")) return "decorating";
  if (c.includes("insulat") || c.includes("sarking") || n.includes("insulation")) return "insulation";
  // Bathroom labour-only line items (must precede generic floor/plumbing matches)
  if (n.includes("sanitaryware install") || n.includes("plumbing fixtures install")) return "sanitaryware_install";
  if (n.includes("bathroom accessor") || (n.includes("accessories") && n.includes("install") && n.includes("labour"))) return "bath_accessories_install";
  if (n.includes("shower screen") || n.includes("shower enclosure") || n.includes("shower door")) return "shower_screen_install";
  // Towel-rail cap intentionally requires explicit "towel" context — a generic
  // "Radiator Installation" in a non-bathroom (heating) job has a higher
  // legitimate ceiling and shouldn't be capped at the bathroom labour rate.
  if (n.includes("towel rail") || n.includes("towel warmer") || (n.includes("radiator") && n.includes("install") && (n.includes("towel") || c.includes("bathroom")))) return "towel_rail_install";
  if (n.includes("waterproof") || n.includes("tanking") || n.includes("pan liner")) return "waterproofing";
  if (n.includes("skip bin") || n.includes("skip hire") || n.includes("dumpster")) return "skip_bin";
  // Tile splits — wall vs floor — must be checked BEFORE the generic "flooring"
  // category check below, otherwise an AI emit of category "floor_tiling" /
  // "floor tiling" returns the broader flooring cap instead of the tile cap.
  if (c.includes("til") && (n.includes("wall til") || n.includes("backsplash"))) return "wall_tiling";
  if ((c.includes("til") || c.includes("floor")) && n.includes("floor til")) return "floor_tiling";
  if (c.includes("til")) return "tiling";
  if (c.includes("floor") && !n.includes("particleboard") && !n.includes("chipboard")) return "flooring";
  if (c.includes("brick") || c.includes("blockwork")) return "brickwork";
  if (n.includes("skirting") || n.includes("architrave") || n.includes("window reveal") || n.includes("window board")) return "carpentry_trim";
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Categorisation guard — fix common AI miscategorisations under "Preliminaries"
// ─────────────────────────────────────────────────────────────────────────────
function fixCategoryIfWrong(item: { name?: string; description?: string; category?: string }): string | undefined {
  const cat = (item.category || "").toLowerCase();
  if (!cat.includes("prelim")) return item.category;

  const blob = `${item.name || ""} ${item.description || ""}`.toLowerCase();
  // Structural framing / floor joists / steel work wrongly tagged Preliminaries
  if (/\b(floor framing|loft floor|floor joists?|joists?|trimmers|hangers|i[- ]?joists?)\b/.test(blob)) return "carpentry";
  if (/\b(stud framing|stud wall|wall framing|partition framing)\b/.test(blob)) return "carpentry";
  if (/\b(steel beam|rsj|ridge beam|padstone|lintel)\b/.test(blob)) return "steelwork";
  if (/\b(plant & equipment|lifting equipment|crane|telehandler|small plant)\b/.test(blob)) return "plant_equipment";
  if (/\b(scaffold|edge protection)\b/.test(blob)) return "scaffolding";
  if (/\b(skip bin|skip hire|waste removal)\b/.test(blob)) return "waste";
  if (/\b(demolition|strip[- ]?out|soft strip)\b/.test(blob)) return "demolition";
  return item.category;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────
export function localiseText(text: string, market?: string | null): string {
  if (market !== "au" || !text) return text;
  let out = text;
  for (const [re, repl] of AU_TERMINOLOGY) {
    out = typeof repl === "function" ? out.replace(re, repl as any) : out.replace(re, repl);
  }
  return out;
}

export function applyAuRateCap<T extends { unitCost?: number; unit?: string; category?: string; name?: string }>(item: T, market?: string | null): T {
  if (market !== "au" || typeof item.unitCost !== "number") return item;
  const key = resolveCapKey(item.category, item.name);
  if (!key) return item;
  const cap = AU_RATE_CAPS[key];
  const u = (item.unit || "").toLowerCase();
  if (!cap.units.includes(u)) return item;
  if (item.unitCost > cap.maxUnitCost) {
    return { ...item, unitCost: cap.maxUnitCost };
  }
  return item;
}

export function fixAuCategory<T extends { name?: string; description?: string; category?: string }>(item: T, market?: string | null): T {
  if (market !== "au") return item;
  const newCat = fixCategoryIfWrong(item);
  if (newCat && newCat !== item.category) return { ...item, category: newCat };
  return item;
}

// One-shot localiser used by the AI pipeline at the very end. Generic over the
// minimal fields any line item is guaranteed to expose.
export function localiseAuLineItems<T extends { name?: string; description?: string; unitCost?: number; unit?: string; category?: string }>(items: T[], market?: string | null): T[] {
  if (market !== "au" || !Array.isArray(items)) return items;
  return items.map(item => {
    let next: T = { ...item };
    if (typeof next.name === "string") next.name = localiseText(next.name, market) as any;
    if (typeof next.description === "string") next.description = localiseText(next.description, market) as any;
    next = fixAuCategory(next, market);
    next = applyAuRateCap(next, market);
    return next;
  });
}
